import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Custom hook for scroll-triggered animations
 * Triggers animation each time element enters viewport
 * 
 * @param {Object} options - IntersectionObserver options
 * @param {number} options.threshold - How much of the element should be visible (0-1)
 * @param {string} options.rootMargin - Margin around the root
 * @param {boolean} options.triggerOnce - If true, animate only once; if false, re-animate on each scroll
 * @returns {[React.RefObject, boolean]} - Ref to attach to element and visibility state
 */
export const useScrollAnimation = (options = {}) => {
  const {
    threshold = 0.15,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = false
  } = options;

  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          } else if (!triggerOnce) {
            // Re-trigger animation when scrolling away and back
            setIsVisible(false);
          }
        });
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [elementRef, isVisible];
};

/**
 * Component wrapper for scroll animations
 * Handles the animation logic internally
 */
export const ScrollAnimateWrapper = ({ 
  children, 
  animationClass = 'animate-fade-in-up',
  className = '',
  style = {},
  delay = 0,
  threshold = 0.15,
  triggerOnce = false,
  as: Component = 'div'
}) => {
  const [ref, isVisible] = useScrollAnimation({ threshold, triggerOnce });

  return (
    <Component
      ref={ref}
      className={`scroll-animate ${animationClass} ${isVisible ? 'is-visible' : ''} ${className}`}
      style={{ 
        ...style, 
        transitionDelay: `${delay}s`
      }}
    >
      {children}
    </Component>
  );
};

export default useScrollAnimation;
